/**
 * Authentication context for managing user authentication state
 */
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authApi, UserResponse } from '../services/authApi';

interface AuthContextType {
  user: UserResponse | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, provider: 'google' | 'microsoft' | 'okta') => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = Boolean(user && authApi.isAuthenticated());

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Check if we have stored auth data
        if (authApi.isAuthenticated()) {
          const storedUser = authApi.getCurrentUserFromStorage();
          if (storedUser) {
            setUser(storedUser);
            // Refresh user data from server to ensure it's current
            try {
              const currentUser = await authApi.getCurrentUser();
              setUser(currentUser);
              // Update stored user data
              authApi.storeAuthData({
                access_token: localStorage.getItem('access_token') || '',
                token_type: 'bearer',
                expires_in: 3600,
                refresh_token: localStorage.getItem('refresh_token') || undefined,
                user: currentUser
              });
            } catch (error) {
              console.error('Failed to refresh user data:', error);
              // If refresh fails, try to refresh token
              const refreshToken = localStorage.getItem('refresh_token');
              if (refreshToken) {
                try {
                  const tokenResponse = await authApi.refreshToken(refreshToken);
                  authApi.storeAuthData(tokenResponse);
                  setUser(tokenResponse.user);
                } catch (refreshError) {
                  console.error('Token refresh failed:', refreshError);
                  authApi.clearAuthData();
                  setUser(null);
                }
              } else {
                authApi.clearAuthData();
                setUser(null);
              }
            }
          }
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
        authApi.clearAuthData();
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async (email: string, provider: 'google' | 'microsoft' | 'okta') => {
    console.log('🔍 AuthContext login called with:', { email, provider });
    try {
      const redirectUri = `${window.location.origin}/auth/callback`;
      const state = generateRandomState();
      
      console.log('🔍 Login setup:', { redirectUri, state });
      
      // Store login state
      sessionStorage.setItem('auth_state', state);
      sessionStorage.setItem('auth_provider', provider);
      sessionStorage.setItem('auth_email', email);
      
      let authUrl: string;
      
      if (provider === 'google') {
        console.log('🔍 Getting Google auth URL...');
        const response = await authApi.getGoogleAuthUrl(redirectUri, state);
        authUrl = response.authorization_url;
        console.log('✅ Got Google auth URL:', authUrl);
      } else if (provider === 'microsoft') {
        console.log('🔍 Getting Microsoft auth URL...');
        const response = await authApi.getMicrosoftAuthUrl(redirectUri, state);
        authUrl = response.authorization_url;
        console.log('✅ Got Microsoft auth URL:', authUrl);
      } else {
        console.log('🔍 Getting Okta auth URL...');
        // For Okta, discover domain first
        const domainResponse = await authApi.discoverDomain(email);
        if (!domainResponse.has_okta_config) {
          throw new Error(`No Okta configuration found for domain: ${domainResponse.email_domain}`);
        }
        
        sessionStorage.setItem('auth_email_domain', domainResponse.email_domain);
        sessionStorage.setItem('auth_organization', domainResponse.organization_name || '');
        
        const response = await authApi.getOktaAuthUrl(email, redirectUri, state);
        authUrl = response.authorization_url;
        console.log('✅ Got Okta auth URL:', authUrl);
      }
      
      // Redirect to OAuth provider
      console.log('🔍 Redirecting to:', authUrl);
      window.location.href = authUrl;
    } catch (error) {
      console.error('❌ Login failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setUser(null);
      // Clear any session storage
      sessionStorage.clear();
      // Redirect to login
      window.location.href = '/login';
    }
  };

  const refreshUser = async () => {
    try {
      if (authApi.isAuthenticated()) {
        const currentUser = await authApi.getCurrentUser();
        setUser(currentUser);
        // Update stored user data
        authApi.storeAuthData({
          access_token: localStorage.getItem('access_token') || '',
          token_type: 'bearer',
          expires_in: 3600,
          refresh_token: localStorage.getItem('refresh_token') || undefined,
          user: currentUser
        });
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

function generateRandomState(): string {
  return Math.random().toString(36).substring(2, 15) + 
         Math.random().toString(36).substring(2, 15);
}
